import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MediaObject } from 'src/api/services/MediaClient';
import { useToggle } from 'src/helpers/useToggle';
import { Api } from 'src/api';
import { download as fallbackDownload } from 'src/lib/utils';
import { InterceptableAxiosRequestConfig, isCancelError } from 'src/api/Http/Http.types';

// Every failure below is reported by the caller, which knows whether it is
// toasting about one file or a whole revision — so Http's GLOBAL error toast
// has to be suppressed or a refused download stacks two red toasts. Same
// reasoning, and the same flag, as useDownloadRequestFiles.
const noGlobalToast: InterceptableAxiosRequestConfig = { _noIntercept: true };

/**
 * Raised when at least one file in the batch could not be downloaded.
 *
 * `message` is only ever something worth showing a customer — the API's own
 * 409 text, or our own line for a blocked popup — and is empty when there is
 * nothing better than the caller's generic wording. Read it through
 * downloadFailureMessage rather than directly.
 */
class MediaDownloadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MediaDownloadError';
  }
}

/**
 * What to put in the failure toast: our own reason when the batch produced one
 * worth showing, the caller's generic line otherwise.
 */
const downloadFailureMessage = (error: unknown, fallback: string): string =>
  error instanceof MediaDownloadError && error.message ? error.message : fallback;

/**
 * Mirrors what Http's error interceptor stays quiet about — a request the app
 * itself cancelled, and a transport that never produced a response.
 *
 * `_noIntercept` buys silence from the global toast and pays for it with that
 * silence: the interceptor's own `isCancelError` / `isAbortError` branch is
 * skipped too, so a logout mid-batch (which rejects every in-flight request
 * with `Request cancelled during logout`) would otherwise be reported to a
 * customer who is already on their way to the login screen. Same guard, for
 * the same reason, as useDownloadRequestFiles.
 */
const isSilentTransportError = (error: unknown): boolean => {
  // `isCancelError` reads `.code` off its argument unguarded, so it must not
  // be handed a null rejection.
  const rejection = (error ?? {}) as { code?: number | string; message?: string };

  return (
    isCancelError(rejection) ||
    rejection.code === 'ECONNABORTED' ||
    !!rejection.message?.toLowerCase().includes('request aborted')
  );
};

const useMediaDownload = () => {
  const { t } = useTranslation();
  const [isDownloading, , toggle] = useToggle();

  /**
   * Resolves true when every file reached the browser, false when the batch
   * was abandoned for a reason the customer should not be told about, and
   * throws a MediaDownloadError when something actually failed. Callers must
   * only announce success on true — see the two download actions on the
   * Revisions page.
   */
  const downloadAll = useCallback(
    async (media: MediaObject[]): Promise<boolean> => {
      toggle();
      try {
        let attempted = 0;
        let failures = 0;
        let reason = '';

        for (const m of media) {
          if (!m.id) continue;

          attempted++;

          try {
            const { download_url } = await Api.media.getDownloadUrl(m.id, noGlobalToast);

            // Null means the browser refused the popup, which is the ordinary
            // outcome rather than the exotic one: this runs after an await, so
            // the click that authorised it is spent and only the tail of the
            // activation window is left. Nothing downloaded, so it counts.
            if (!window.open(download_url, '_blank')) {
              failures++;
              reason =
                reason ||
                t('Your browser blocked the download. Allow pop-ups for this site and try again.');
            }

            continue;
          } catch (error) {
            const { code, message } = (error ?? {}) as { code?: number | string; message?: string };

            if (isSilentTransportError(error)) {
              return false;
            }

            // Fallback to legacy fetch+blob if the endpoint is unavailable.
            // Archived media never has an index_url — MediaResource blanks it
            // once the object leaves the main bucket — so this is the
            // hot-storage path only, and a cold file falls straight through to
            // the count below.
            if (m.index_url) {
              try {
                await fallbackDownload(m.index_url);
                continue;
              } catch {
                // Both routes are out; fall through and report it.
              }
            }

            failures++;
            // Only the 409 carries wording written for a customer — it is the
            // endpoint's refusal for a file that is cold, restoring, or whose
            // restored copy has expired, and it says what to do next. Anything
            // else is a server or transport message ("Server Error", "No query
            // results for model [App\Models\Media] 9f3…") that would be worse
            // than the caller's generic line.
            reason = reason || (code === 409 && message ? message : '');
          }
        }

        // Silence here used to be indistinguishable from success: the caller's
        // "downloaded successfully" toast fired for a batch in which nothing
        // reached the browser. An empty batch counts — the "All current version
        // files" action is only guarded by `hasMedia`, which stays true for a
        // revision whose media are all outside the downloadable collections.
        if (failures > 0 || attempted === 0) {
          throw new MediaDownloadError(reason);
        }

        return true;
      } finally {
        toggle();
      }
    },
    [t],
  );

  return {
    downloadAll,
    isDownloading,
  };
};

export { useMediaDownload, MediaDownloadError, downloadFailureMessage };
